import React from 'react'
import styles from './style.module.scss'
import AgendaCell from 'pages/calendar/components/AgendaCell'
import DateNavigater from 'pages/calendar/components/DateNavigater'
import { useEffect, useRef, useState, FC } from 'react'
import emptyAgendaIcon from 'assets/images/calendar/no-events.svg'
import * as calendarService from 'pages/calendar/modules/CalendarService'
import * as dateUtils from 'pages/calendar/utils/DateUtils'
import { useTranslation } from 'react-i18next'
import { getTodoInstances } from '../../modules/TodoUtils'
import * as todoSync from 'pages/todo/modules/TodoSync'
import settings from 'popups/Settings'
import { Moment } from 'moment'

interface Todo {
  status?: string
}

interface CalendarInstance {
  id: string
  subject: string | null
  startDate: Date
  endDate: Date
  color: string
  todo?: Todo
}

interface HasEventIndex {
  index: number
  date: Date
}

interface MobileMonthBoxProps {
  browseDate: Date
  focusedDate: Date
  onDateChange: (date: Date | Moment) => void
  onEventClick: (event: any, element: HTMLDivElement) => void
}

const MobileMonthBox: FC<MobileMonthBoxProps> = ({
  browseDate,
  focusedDate,
  onDateChange,
  onEventClick,
}) => {
  const { t } = useTranslation()
  const [focusedInstances, setFocusedInstances] = useState<CalendarInstance[]>([])
  const [hasEventIndexes, setHasEventIndexes] = useState<HasEventIndex[]>([])
  const faceDate = useRef(focusedDate)
  const isMountedRef = useRef(true)
  const locale = settings.getValue('general').locale

  const _calcInfo = async () => {
    // 언마운트된 인스턴스는 계산/렌더를 건너뛴다.
    // 라우터 전환 등으로 남은 유령 인스턴스가 emit마다 setState를 호출해
    // 실제 보이는 인스턴스의 렌더를 방해하는 것을 방지한다.
    if (!isMountedRef.current) {
      return
    }
    const inFocusDate = faceDate.current
    const inFocusLastDate = dateUtils.dateAddingDate(inFocusDate, 1)
    const inFocusTime = inFocusDate.getTime()
    const inFocusLastTime = inFocusLastDate.getTime()
    const instances = calendarService.getInstances()
    const focusedInstances = instances.filter((instance: CalendarInstance) => {
      return (
        instance.startDate.getTime() < inFocusLastTime && instance.endDate.getTime() > inFocusTime
      )
    })
    const todoInstances = await getTodoInstances(inFocusDate, inFocusLastDate)
    if (todoInstances.length > 0) {
      focusedInstances.push(...todoInstances)
    }
    const sorted = focusedInstances.sort((a, b) => {
      if (a.startDate.getTime() !== b.startDate.getTime()) {
        return a.startDate.getTime() - b.startDate.getTime()
      }
      if (a.todo && !b.todo) {
        return 1
      }
      if (b.todo && !a.todo) {
        return 1
      }
      if (a.todo?.status !== b.todo?.status) {
        return a.todo?.status === 'COMPLETED' ? 1 : -1
      }
      const aSubject = a.subject || ''
      const bSubject = b.subject || ''
      return aSubject.localeCompare(bSubject, locale)
    })
    if (!isMountedRef.current) {
      return
    }
    setFocusedInstances(sorted)
    const hasEventIndexes = await getHasEventDate(inFocusDate)
    if (!isMountedRef.current) {
      return
    }
    setHasEventIndexes(hasEventIndexes)
  }

  useEffect(() => {
    faceDate.current = dateUtils.getDateZero(focusedDate)
    _calcInfo()
  }, [focusedDate])

  const _notiCallback = () => {
    _calcInfo()
  }

  useEffect(() => {
    isMountedRef.current = true
    calendarService.addInstanceListener(_notiCallback)
    todoSync.addTodoSyncListener(_notiCallback)

    return () => {
      isMountedRef.current = false
      calendarService.removeInstanceListener(_notiCallback)
      todoSync.removeTodoSyncListener(_notiCallback)
    }
  }, [])

  const getHasEventDate = async (date: Date): Promise<HasEventIndex[]> => {
    const instances = calendarService.getInstances()
    let monthFirstDate = dateUtils.getMonthViewFirstDate(date)
    let monthLastDate = dateUtils.getMonthViewLastDate(date)
    let loopDate = new Date(monthFirstDate)
    let loopInstances = [...instances]
    let index = 0
    let eventIndexes: HasEventIndex[] = []
    const todoInstances = await getTodoInstances(monthFirstDate, monthLastDate)
    if (todoInstances.length > 0) {
      loopInstances.push(...todoInstances)
      loopInstances.sort((a, b) => {
        return a.startDate.getTime() - b.startDate.getTime()
      })
    }

    do {
      const loopTime = loopDate.getTime()
      const loopLastTime = dateUtils.dateAddingDate(loopDate, 1).getTime()

      loopInstances = loopInstances.filter((instance) => {
        return instance.endDate.getTime() >= loopTime
      })

      let hasEvent = false
      loopInstances.every((instance) => {
        if (instance.startDate.getTime() >= loopLastTime) {
          return false
        }
        if (instance.startDate.getTime() < loopLastTime && instance.endDate.getTime() > loopTime) {
          hasEvent = true
          return false
        }
        return true
      })

      if (hasEvent) {
        eventIndexes.push({ index, date: loopDate })
      }
      index++
      loopDate = dateUtils.dateAddingDate(loopDate, 1)
    } while (loopDate.getTime() <= monthLastDate.getTime())

    return eventIndexes
  }

  const handleEventClick = (
    instance: CalendarInstance,
    date: Date,
    inRef: React.RefObject<HTMLDivElement>,
  ) => {
    const event = {
      instanceId: instance.id,
      date: date,
      instance: instance,
      todo: instance.todo,
      isTodo: instance.todo != null,
    }
    onEventClick(event, inRef.current!)
  }

  return (
    <div className={styles.mobileMonthBox}>
      <DateNavigater
        {...({
          date: browseDate,
          focusedDate: focusedDate,
          viewType: 'day',
          onDateChange: onDateChange,
          onMonthChange: onDateChange,
          hasEventIndexes: hasEventIndexes,
        } as any)}
      />
      <div style={{ height: 1, background: '#F0F1F3' }} />
      {focusedInstances.length > 0 ? (
        <div className={styles.agendaList}>
          {focusedInstances.map((instance) => {
            return (
              <AgendaCell
                key={instance.id}
                event={instance as any}
                date={focusedDate}
                onEventClick={handleEventClick as any}
              />
            )
          })}
        </div>
      ) : (
        <div className={styles.emptyAgendaList}>
          <img className={styles.icon} src={emptyAgendaIcon} alt="empty-agenda" />
          <div className={styles.title}>{t('calendar.detail.noEvents')}</div>
        </div>
      )}
    </div>
  )
}

export default MobileMonthBox
